import { useEffect, useRef, useState } from "react";

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_DUPLO_CLIQUE = 2.5;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Aceita tanto strings (url de foto, usado no Orçamento) quanto objetos
// {url, tipo:"video"} (galeria de Produtos, que pode ter vídeo/gif).
const normalizar = (foto) => typeof foto === "string" ? { url: foto, tipo: "imagem" } : foto;

// Visualizador em tela cheia com navegação entre fotos/vídeos e zoom (roda do
// mouse, duplo-clique/duplo-toque, pinça e arrastar quando ampliado — só pra
// foto; vídeo usa os controles nativos e não tem zoom). Sem lib externa.
export function Lightbox({ fotos = [], indiceInicial = 0, aberto, onFechar }) {
  const [indice, setIndice] = useState(indiceInicial);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const arrastandoRef = useRef(null); // {x,y} do ponteiro no mousedown, relativo ao pos atual
  const pinchRef = useRef(null); // {dist, zoomBase} no início da pinça
  const swipeRef = useRef(null); // {x,y,time} no início do toque único
  const ultimoTapRef = useRef(0);

  useEffect(() => {
    if (aberto) { setIndice(indiceInicial); setZoom(1); setPos({ x:0, y:0 }); }
  }, [aberto, indiceInicial]);

  const irPara = (i) => {
    setIndice((i + fotos.length) % fotos.length);
    setZoom(1); setPos({ x:0, y:0 });
  };

  useEffect(() => {
    if (!aberto) return;
    const onKey = (e) => {
      if (e.key === "Escape") onFechar?.();
      else if (e.key === "ArrowLeft" && fotos.length > 1) irPara(indice - 1);
      else if (e.key === "ArrowRight" && fotos.length > 1) irPara(indice + 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [aberto, indice, fotos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!aberto || fotos.length === 0) return null;

  const atual = normalizar(fotos[indice]);
  const ehVideo = atual.tipo === "video";

  const alternarZoom = (clientX, clientY, rect) => {
    if (ehVideo) return;
    if (zoom > 1) { setZoom(1); setPos({ x:0, y:0 }); return; }
    // Amplia centrado no ponto clicado/tocado
    const offX = (rect.width / 2 - (clientX - rect.left)) * (ZOOM_DUPLO_CLIQUE - 1);
    const offY = (rect.height / 2 - (clientY - rect.top)) * (ZOOM_DUPLO_CLIQUE - 1);
    setZoom(ZOOM_DUPLO_CLIQUE);
    setPos({ x: offX, y: offY });
  };

  const onWheel = (e) => {
    if (ehVideo) return;
    e.preventDefault();
    setZoom(z => {
      const novo = clamp(z + (e.deltaY < 0 ? 0.35 : -0.35), ZOOM_MIN, ZOOM_MAX);
      if (novo === 1) setPos({ x:0, y:0 });
      return novo;
    });
  };

  const onMouseDown = (e) => {
    if (zoom <= 1) return;
    arrastandoRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };
  const onMouseMove = (e) => {
    if (!arrastandoRef.current) return;
    setPos({ x: e.clientX - arrastandoRef.current.x, y: e.clientY - arrastandoRef.current.y });
  };
  const pararArraste = () => { arrastandoRef.current = null; };

  const distancia = (touches) => {
    const [a, b] = touches;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { dist: distancia(e.touches), zoomBase: zoom };
      swipeRef.current = null;
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      swipeRef.current = { x: t.clientX, y: t.clientY, time: Date.now(), pos0: pos };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current && !ehVideo) {
      e.preventDefault();
      const nova = distancia(e.touches);
      const novoZoom = clamp(pinchRef.current.zoomBase * (nova / pinchRef.current.dist), ZOOM_MIN, ZOOM_MAX);
      setZoom(novoZoom);
    } else if (e.touches.length === 1 && swipeRef.current && zoom > 1) {
      const t = e.touches[0];
      setPos({ x: swipeRef.current.pos0.x + (t.clientX - swipeRef.current.x), y: swipeRef.current.pos0.y + (t.clientY - swipeRef.current.y) });
    }
  };
  const onTouchEnd = (e) => {
    pinchRef.current = null;
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s) return;
    const dt = Date.now() - s.time;
    const rect = e.currentTarget.getBoundingClientRect();

    // Duplo-toque: dois "toques rápidos" no mesmo lugar em menos de 300ms
    if (dt < 250) {
      const agora = Date.now();
      if (agora - ultimoTapRef.current < 300) {
        alternarZoom(s.x, s.y, rect);
        ultimoTapRef.current = 0;
        return;
      }
      ultimoTapRef.current = agora;
    }

    // Swipe horizontal (só quando não ampliado) troca de foto
    if (zoom === 1 && fotos.length > 1) {
      const ultimo = e.changedTouches[0];
      const dx = ultimo.clientX - s.x;
      const dy = ultimo.clientY - s.y;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        irPara(indice + (dx < 0 ? 1 : -1));
      }
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Visualizar mídia"
      onClick={onFechar}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", touchAction:"none" }}>
      <button onClick={onFechar} title="Fechar (Esc)"
        style={{ position:"absolute", top:14, right:16, width:36, height:36, borderRadius:"50%", background:"rgba(255,255,255,0.12)", border:"none", color:"#fff", fontSize:18, fontWeight:900, cursor:"pointer", zIndex:1 }}>✕</button>

      {fotos.length > 1 && (
        <button onClick={e=>{ e.stopPropagation(); irPara(indice-1); }}
          style={{ position:"absolute", top:"50%", left:14, transform:"translateY(-50%)", width:44, height:44, borderRadius:"50%", border:"none", background:"rgba(255,255,255,0.12)", color:"#fff", fontSize:22, fontWeight:900, cursor:"pointer" }}>‹</button>
      )}

      <div
        onClick={e => e.stopPropagation()}
        onDoubleClick={e => alternarZoom(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect())}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={pararArraste}
        onMouseLeave={pararArraste}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ width:"min(92vw,900px)", height:"min(82vh,900px)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
        {ehVideo ? (
          <video src={atual.url} controls autoPlay style={{ maxWidth:"100%", maxHeight:"100%" }} />
        ) : (
          <img src={atual.url} alt={`foto ${indice+1} de ${fotos.length}`} draggable={false}
            style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain", userSelect:"none",
              cursor: zoom > 1 ? (arrastandoRef.current ? "grabbing" : "grab") : "zoom-in",
              transform:`translate(${pos.x}px, ${pos.y}px) scale(${zoom})`, transition: arrastandoRef.current ? "none" : "transform 0.15s" }} />
        )}
      </div>

      {fotos.length > 1 && (
        <button onClick={e=>{ e.stopPropagation(); irPara(indice+1); }}
          style={{ position:"absolute", top:"50%", right:14, transform:"translateY(-50%)", width:44, height:44, borderRadius:"50%", border:"none", background:"rgba(255,255,255,0.12)", color:"#fff", fontSize:22, fontWeight:900, cursor:"pointer" }}>›</button>
      )}

      {fotos.length > 1 && (
        <div onClick={e=>e.stopPropagation()} style={{ position:"absolute", bottom:18, left:"50%", transform:"translateX(-50%)", display:"flex", gap:7 }}>
          {fotos.map((_, i) => (
            <button key={i} onClick={()=>irPara(i)}
              style={{ width:8, height:8, borderRadius:"50%", border:"none", padding:0, cursor:"pointer", background:i===indice?"#fff":"rgba(255,255,255,0.35)" }} />
          ))}
        </div>
      )}
    </div>
  );
}
